/**
 * New Audit Run page (/inventory/audit/new-run) — the dedicated upload surface
 * from the approved remodel mockup, following the Update Inventory page
 * pattern. Drop all of today's exports anywhere on the page and INVI sorts
 * them into slots by the filename patterns the backend already recognizes
 * (POST /audit/upload detects the vendor server-side). The run button explains
 * what it's waiting on in words; when every slot reads Staged it goes live,
 * and a finished run drops you back on the audit worklist with the new run
 * selected (the audit page always opens on the group's latest run).
 *
 * The content component is keyed by dealer group: switching groups remounts
 * it, so in-flight uploads and refreshes from the old group can never paint
 * their results over the new group's state.
 */
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"

import Layout from "../../components/Layout"
import AuditActiveFilesPanel from "../../components/audit/AuditActiveFilesPanel"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import UploadSlotsGrid, { describeWaitingOn } from "../../components/audit/newrun/UploadSlotsGrid"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { THEME } from "../../config/theme"
import { formatElapsed } from "../../utils/formatAge"
import { uploadLanded } from "../../utils/uploadRecovery"
import {
  AuditFileStatus,
  AuditUploadedFile,
  deleteAuditFile,
  fetchAuditFileStatus,
  listAuditFiles,
  listAuditRuns,
  runInventoryAudit,
  uploadAuditFile,
  waitForAuditRunJob,
} from "../../api/audit"

interface UploadRow {
  /** Unique per drop — duplicate filenames across drops must not collide. */
  id: number
  name: string
  /** Shown so a slow upload reads as a big file rather than a stuck one — the
   *  Tekion export is ~90 MB and holds the queue for a while on its own. */
  sizeBytes: number
  state: "queued" | "uploading" | "error"
  /** Fraction of bytes sent, 0..1 — live movement is what separates "big
   *  file" from "dead transfer" for the operator. */
  progress?: number
  message?: string
}


/** The two stages of a run, with the live one marked and a clock that counts
 *  up. The stage is not estimated: it flips when the inventory run appears in
 *  the database, which is the real moment the website scrape starts. */
export function RunProgress({
  stage,
  elapsedSeconds,
}: {
  stage: "classifying" | "website"
  elapsedSeconds: number
}) {
  const steps = [
    { key: "classifying" as const, label: "Classifying vendor files" },
    { key: "website" as const, label: "Checking the live website" },
  ]
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        fontSize: 12.5,
        color: THEME.colors.neutral.light.textSecondary,
      }}
    >
      {steps.map((step, i) => {
        const done = step.key === "classifying" && stage === "website"
        const active = step.key === stage
        return (
          <React.Fragment key={step.key}>
            {i > 0 && <span aria-hidden>→</span>}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: active
                  ? THEME.colors.neutral.light.textPrimary
                  : THEME.colors.neutral.light.textSecondary,
                fontWeight: active ? 600 : 400,
              }}
            >
              <span aria-hidden style={{ fontSize: 11 }}>{done ? "✓" : active ? "●" : "○"}</span>
              {step.label}
              {active && "…"}
            </span>
          </React.Fragment>
        )
      })}
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatElapsed(elapsedSeconds)}</span>
    </div>
  )
}

/** "12.3 MB" / "480 KB" — enough to explain a slow upload. */
function describeSize(bytes: number): string {
  const kb = bytes / 1024
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(kb))} KB`
}

/** "Uploading 43%" while bytes move; "Finishing up" once they've all been
 *  sent and the server is saving the file. */
function describeUploadState(row: UploadRow): string {
  if (row.state !== "uploading") return "Waiting"
  if (row.progress === undefined) return "Uploading"
  if (row.progress >= 1) return "Finishing up"
  return `Uploading ${Math.floor(row.progress * 100)}%`
}

export default function NewAuditRunPage() {
  const { dealerGroupId } = useDealerGroup()
  return (
    <Layout>
      {dealerGroupId === null ? (
        <div style={{ padding: THEME.spacing.lg }}>
          <DealerGroupRequiredNotice surface="audit uploads" />
        </div>
      ) : (
        <NewAuditRunContent key={dealerGroupId} dealerGroupId={dealerGroupId} />
      )}
    </Layout>
  )
}

function NewAuditRunContent({ dealerGroupId }: { dealerGroupId: number }) {
  const navigate = useNavigate()

  const [status, setStatus] = useState<AuditFileStatus | null>(null)
  const [files, setFiles] = useState<AuditUploadedFile[]>([])
  const [uploads, setUploads] = useState<UploadRow[]>([])
  const [dragging, setDragging] = useState(false)
  const [running, setRunning] = useState(false)
  // Which half of the run is happening, and for how long. The pipeline runs
  // server-side (POST /audit/run returns a job we poll to completion), and
  // the inventory run is COMMITTED before the website chain starts
  // (routers/audit.py::_run_audit_with_chain), so polling for its row to
  // appear reports the real handover rather than guessing from a stopwatch.
  // Elapsed time is what makes the wait look alive at all — the
  // classification plus a live scrape of ~2,000 listings runs for minutes.
  const [stage, setStage] = useState<"classifying" | "website">("classifying")
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const runStartRunId = useRef<number | null>(null)
  const [runError, setRunError] = useState<string | null>(null)
  // The chained website check can fail without failing the run — the operator
  // must see that, or an empty website section reads as "no site problems".
  const [websiteWarning, setWebsiteWarning] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const nextUploadId = useRef(1)
  const errorTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())

  const refresh = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([fetchAuditFileStatus(dealerGroupId), listAuditFiles(dealerGroupId)])
      setStatus(s)
      setFiles(f)
    } catch {
      // Transient failure: keep showing the last known staging state rather
      // than wiping the slots to "everything waiting".
    }
  }, [dealerGroupId])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [s, f] = await Promise.all([fetchAuditFileStatus(dealerGroupId), listAuditFiles(dealerGroupId)])
        if (cancelled) return
        setStatus(s)
        setFiles(f)
      } catch {
        // Slots simply show everything as waiting until a later refresh lands.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId])

  // Tick the elapsed clock while a run is in flight.
  useEffect(() => {
    if (!running) return
    const id = setInterval(() => setElapsedSeconds((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [running])

  // Watch for the inventory run to land, which is the moment classification
  // finished and the website scrape began. Stops polling once it flips.
  useEffect(() => {
    if (!running || stage !== "classifying") return
    let cancelled = false
    const id = setInterval(async () => {
      try {
        const response = await listAuditRuns(1, dealerGroupId)
        const latest = response.runs.find((r) => r.run_type === "inventory")
        if (cancelled || !latest) return
        if (runStartRunId.current === null || latest.id > runStartRunId.current) {
          setStage("website")
        }
      } catch {
        // A failed poll only means the stage label lags; the run is unaffected.
      }
    }, 4000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [running, stage, dealerGroupId])

  // Clear any pending error-row timers when the page unmounts.
  useEffect(() => {
    const timers = errorTimers.current
    return () => {
      timers.forEach(clearTimeout)
      timers.clear()
    }
  }, [])

  const handleFiles = useCallback(
    async (picked: FileList | File[]) => {
      const batch = Array.from(picked).map((file) => ({ file, id: nextUploadId.current++ }))
      // Show the WHOLE batch at once. Uploads run one at a time, and adding
      // each row only as its own upload began meant dropping eight files
      // looked like one file: the operator could not see what was still to
      // come, which is most of the "is it doing anything?" problem.
      setUploads((rows) => [
        ...rows,
        ...batch.map(({ file, id }) => ({
          id,
          name: file.name,
          sizeBytes: file.size,
          state: "queued" as const,
        })),
      ])
      for (const { file, id } of batch) {
        setUploads((rows) =>
          rows.map((r) => (r.id === id ? { ...r, state: "uploading" as const } : r))
        )
        // Snapshot the staged list first: if the upload's success reply gets
        // lost mid-flight, comparing a fresh list against this one tells us
        // whether the file landed anyway.
        let stagedBefore: AuditUploadedFile[] | null = null
        try {
          stagedBefore = await listAuditFiles(dealerGroupId)
        } catch {
          stagedBefore = null
        }
        // Progress events fire constantly; only re-render on whole-percent moves.
        let lastShownPercent = -1
        const onProgress = (fraction: number) => {
          const percent = Math.floor(fraction * 100)
          if (percent === lastShownPercent) return
          lastShownPercent = percent
          setUploads((rows) => rows.map((r) => (r.id === id ? { ...r, progress: fraction } : r)))
        }
        try {
          await uploadAuditFile(file, dealerGroupId, onProgress)
          setUploads((rows) => rows.filter((r) => r.id !== id))
        } catch (err) {
          // A long upload's reply can die AFTER the file was saved (store
          // networks drop long-lived connections — the 85 MB Tekion export hit
          // exactly this). Ask the server before reporting failure, so the
          // operator never has to navigate away to learn it actually worked.
          const landed = stagedBefore
            ? await listAuditFiles(dealerGroupId)
                .then((after) => uploadLanded(file.name, stagedBefore as AuditUploadedFile[], after))
                .catch(() => false)
            : false
          if (landed) {
            setUploads((rows) => rows.filter((r) => r.id !== id))
          } else {
            const message = err instanceof Error ? err.message : "Upload failed"
            setUploads((rows) => rows.map((r) => (r.id === id ? { ...r, state: "error" as const, message } : r)))
            // Errors linger briefly so the operator sees which file was rejected.
            const timer = setTimeout(() => {
              errorTimers.current.delete(timer)
              setUploads((rows) => rows.filter((r) => r.id !== id))
            }, 6000)
            errorTimers.current.add(timer)
          }
        }
        await refresh()
      }
    },
    [dealerGroupId, refresh]
  )

  // Drop-anywhere is the page's promise: window-level handlers catch drags
  // over the chrome (sidebar, top bar, gutters) so a slightly-missed drop
  // can't fire the browser default and navigate the tab to the CSV.
  useEffect(() => {
    const isFileDrag = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files")
    const onDragOver = (e: DragEvent) => {
      if (!isFileDrag(e)) return
      e.preventDefault()
      setDragging(true)
    }
    const onDrop = (e: DragEvent) => {
      e.preventDefault()
      setDragging(false)
      if (e.dataTransfer && e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files)
    }
    const onDragLeave = (e: DragEvent) => {
      // relatedTarget is null when the drag leaves the window entirely.
      if (e.relatedTarget === null) setDragging(false)
    }
    const onDragEnd = () => setDragging(false)
    window.addEventListener("dragover", onDragOver)
    window.addEventListener("drop", onDrop)
    window.addEventListener("dragleave", onDragLeave)
    window.addEventListener("dragend", onDragEnd)
    return () => {
      window.removeEventListener("dragover", onDragOver)
      window.removeEventListener("drop", onDrop)
      window.removeEventListener("dragleave", onDragLeave)
      window.removeEventListener("dragend", onDragEnd)
    }
  }, [handleFiles])

  const handleDelete = useCallback(
    async (filename: string) => {
      if (!window.confirm(`Delete "${filename}"?`)) return
      try {
        await deleteAuditFile(filename, dealerGroupId)
      } catch {
        // The refresh below re-syncs whichever state the delete left behind.
      }
      await refresh()
    },
    [dealerGroupId, refresh]
  )

  const handleRun = useCallback(async () => {
    if (!status?.can_run || running) return
    setRunning(true)
    setStage("classifying")
    setElapsedSeconds(0)
    setRunError(null)
    setWebsiteWarning(null)
    try {
      // Remember the newest run BEFORE starting, so the poll above can tell
      // this run's row from the one already there.
      try {
        const before = await listAuditRuns(1, dealerGroupId)
        runStartRunId.current =
          before.runs.find((r) => r.run_type === "inventory")?.id ?? null
      } catch {
        // Unknown baseline: the poll then treats the first inventory run it
        // sees as this one, which is correct for a group's first-ever run.
        runStartRunId.current = null
      }
      // Start the run server-side, then poll its job row to completion — the
      // browser no longer holds a connection open for the whole pipeline, so
      // a proxy timeout or a dropped network can't make a finished audit
      // look failed (2026-09-22: both happened, duplicating the day's runs).
      const job = await runInventoryAudit(dealerGroupId)
      const finished = await waitForAuditRunJob(job.id)
      if (finished.status === "failed" || finished.result === null) {
        setRunError(finished.error ?? "Audit run failed")
        setRunning(false)
        return
      }
      const result = finished.result
      if (result.website_status === "failed") {
        // The audit saved; only the site check failed. Stop here so the
        // operator KNOWS the worklist's website groups are absent, not clean.
        setRunning(false)
        setWebsiteWarning(
          result.website_error ??
            "The website check failed — the audit itself finished and is unaffected."
        )
        return
      }
      // The audit page opens on the group's latest run — which is this one.
      navigate("/inventory/audit")
    } catch (err) {
      setRunError(err instanceof Error ? err.message : "Audit run failed")
      setRunning(false)
    }
  }, [dealerGroupId, status, running, navigate])

  const waiting = describeWaitingOn(status)
  const canRun = Boolean(status?.can_run) && !running

  return (
    <div style={{ padding: THEME.spacing.lg, maxWidth: 1160, margin: "0 auto" }}>
      <Link
        to="/inventory/audit"
        style={{
          fontSize: 12.5,
          color: THEME.colors.neutral.light.textSecondary,
          textDecoration: "none",
        }}
      >
        ← Inventory Audit
      </Link>
      <h1
        style={{
          fontSize: 24,
          fontWeight: 500,
          letterSpacing: "-0.5px",
          margin: `4px 0 ${THEME.spacing.md}`,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        New Audit Run
      </h1>

      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault() // Space must not scroll the page.
            fileInputRef.current?.click()
          }
        }}
        style={{
          border: `1.5px dashed ${THEME.colors.semantic.info}`,
          borderRadius: THEME.borderRadius.md,
          background: THEME.problemDisplay.severitySoft.info,
          outline: dragging ? `2px solid ${THEME.colors.semantic.info}` : "none",
          padding: 18,
          textAlign: "center",
          fontSize: 13,
          color: THEME.colors.neutral.light.textSecondary,
          marginBottom: THEME.spacing.sm,
          cursor: "pointer",
        }}
      >
        <b style={{ color: THEME.colors.neutral.light.textPrimary }}>
          {dragging ? "Drop to stage the files" : "Drop all of today's exports here"}
        </b>{" "}
        — INVI sorts them into the right slot by filename. Or click to browse.
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xls,.xlsx"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files)
          e.target.value = ""
        }}
      />

      {uploads.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: THEME.spacing.sm }}>
          {uploads.map((row) => (
            <div
              key={row.id}
              style={{
                fontSize: 12,
                color:
                  row.state === "error" ? THEME.colors.semantic.danger : THEME.colors.neutral.light.textSecondary,
              }}
            >
              {row.state === "error"
                ? `${row.name} — ${row.message}`
                : `${describeUploadState(row)} · ${row.name} · ${describeSize(row.sizeBytes)}`}
            </div>
          ))}
        </div>
      )}

      <UploadSlotsGrid status={status} onDeleteFile={handleDelete} />

      {/* The flow's terminal action anchors bottom-RIGHT (platform grammar:
          title left, actions right), with the waiting words reading into it. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: THEME.spacing.sm,
        }}
      >
        {waiting.length > 0 && (
          <span style={{ fontSize: 12.5, color: THEME.colors.neutral.light.textSecondary }}>
            Waiting on:{" "}
            {waiting.map((part, i) => (
              <React.Fragment key={part}>
                {i > 0 && " · "}
                <strong style={{ color: THEME.colors.neutral.light.textPrimary, fontWeight: 600 }}>{part}</strong>
              </React.Fragment>
            ))}
          </span>
        )}
        {running && <RunProgress stage={stage} elapsedSeconds={elapsedSeconds} />}
        {runError && (
          <span style={{ fontSize: 12.5, color: THEME.colors.semantic.danger, maxWidth: 420 }}>{runError}</span>
        )}
        <button
          type="button"
          onClick={handleRun}
          disabled={!canRun}
          style={{
            fontSize: 13,
            fontWeight: 500,
            fontFamily: THEME.fonts.primary,
            padding: "8px 16px",
            border: `1px solid ${THEME.colors.navy}`,
            borderRadius: THEME.borderRadius.sm,
            background: THEME.colors.navy,
            color: THEME.colors.white,
            cursor: canRun ? "pointer" : "not-allowed",
            opacity: canRun ? 1 : 0.55,
          }}
        >
          {running ? "Running…" : "Run audit"}
        </button>
      </div>

      {websiteWarning && (
        <div
          style={{
            marginTop: THEME.spacing.sm,
            border: `1px solid ${THEME.colors.semantic.warning}`,
            background: THEME.problemDisplay.severitySoft.warning,
            borderRadius: THEME.borderRadius.md,
            padding: "10px 14px",
            fontSize: 12.5,
            display: "flex",
            alignItems: "center",
            gap: THEME.spacing.sm,
            flexWrap: "wrap",
          }}
        >
          <span style={{ flex: 1, minWidth: 240 }}>
            <strong>Audit finished, but the website check failed:</strong> {websiteWarning}{" "}
            The worklist will show feed problems only for this run.
          </span>
          <button
            type="button"
            onClick={() => navigate("/inventory/audit")}
            style={{
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: THEME.fonts.primary,
              padding: "6px 12px",
              border: `1px solid ${THEME.colors.navy}`,
              borderRadius: THEME.borderRadius.sm,
              background: THEME.colors.navy,
              color: THEME.colors.white,
              cursor: "pointer",
            }}
          >
            Continue to worklist
          </button>
        </div>
      )}

      <div style={{ marginTop: THEME.spacing.md }}>
        <AuditActiveFilesPanel dealerGroupId={dealerGroupId} files={files} onFileDeleted={refresh} />
      </div>
    </div>
  )
}
